// ---------------------------------------------------------------------------
// SpeedyDock SDK — Modal embed
// ---------------------------------------------------------------------------

import { SDK_PREFIX } from "./styles";
import type { ModalOptions, EmbedInstance, EmbedEventType, EmbedEventHandler } from "./types";
import {
  buildEmbedUrl,
  resolveElement,
  createElement,
  createIframe,
  createSpinner,
  hideSpinner,
  EventEmitter,
  listenToIframe,
} from "./utils";

export function createModalEmbed(
  baseUrl: string,
  shortName: string,
  options: ModalOptions = {},
  _autoOpen = false,
): EmbedInstance {
  const emitter = new EventEmitter();
  const src = buildEmbedUrl(baseUrl, shortName, options);

  // Overlay
  const overlay = createElement("div", `${SDK_PREFIX}-overlay`);

  // Modal container
  const modal = createElement("div", `${SDK_PREFIX}-modal`);
  if (options.maxWidth) modal.style.maxWidth = options.maxWidth;

  // Close button
  const closeBtn = createElement("button", `${SDK_PREFIX}-modal-close`);
  closeBtn.innerHTML = "&#10005;";
  closeBtn.setAttribute("aria-label", "Close booking");
  modal.appendChild(closeBtn);

  // Spinner (iframe deferred until first open)
  const spinner = createSpinner();
  modal.appendChild(spinner);

  document.body.appendChild(overlay);
  document.body.appendChild(modal);

  let iframe: HTMLIFrameElement | null = null;
  let unlisten: (() => void) | null = null;

  function ensureIframe(): HTMLIFrameElement {
    if (!iframe) {
      iframe = createIframe(src, "eager");
      modal.appendChild(iframe);
      unlisten = listenToIframe(iframe, emitter, {
        onReady: () => hideSpinner(spinner),
        onClose: () => close(),
      });
    }
    return iframe;
  }

  function open(): void {
    ensureIframe();
    overlay.classList.add("open");
    modal.classList.add("open");
    document.body.style.overflow = "hidden";
  }

  function close(): void {
    overlay.classList.remove("open");
    modal.classList.remove("open");
    document.body.style.overflow = "";
  }

  // Wire trigger
  const trigger = resolveElement(options.trigger);
  const triggerHandler = () => open();
  if (trigger) {
    trigger.addEventListener("click", triggerHandler);
  }

  // Close on overlay click or close button
  overlay.addEventListener("click", close);
  closeBtn.addEventListener("click", close);

  // Close on Escape
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", escHandler);

  // Auto-open immediately if requested (for openModal)
  if (_autoOpen) open();

  return {
    destroy() {
      unlisten?.();
      trigger?.removeEventListener("click", triggerHandler);
      document.removeEventListener("keydown", escHandler);
      overlay.remove();
      modal.remove();
      document.body.style.overflow = "";
    },
    on<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.on(event, handler);
    },
    off<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>) {
      emitter.off(event, handler);
    },
    navigate(path: string) {
      ensureIframe().contentWindow?.postMessage(
        { type: "speedydock:parent-navigate", data: { path } },
        "*",
      );
    },
  };
}

/** Open a modal immediately (no trigger element) */
export function openModal(
  baseUrl: string,
  shortName: string,
  options: Omit<ModalOptions, "trigger"> = {},
): EmbedInstance {
  return createModalEmbed(baseUrl, shortName, options, true);
}
